// Settings → Notifications (IMPLEMENTATION_PLAN §3.5 P2-1): per alert type on/off, rule options, and a rule
// editor modelled on the TMS one — Channels (Mobile App Inbox, Mobile App Push, Email) and Recipients (per
// channel: enabled, allowed roles, severity filter). No quiet hours / throttle (omitted for now). Plus the
// delivery log. Managers only (RLS enforces it).
import EmailIcon from '@mui/icons-material/Email';
import InboxIcon from '@mui/icons-material/Inbox';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { type ReactElement, type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '../../auth/session';
import { Loading, LoadError, Page } from '../../components/Page';
import {
  type Channel,
  CHANNELS,
  REQUEST_KINDS,
  type Delivery,
  type Role,
  ROLES,
  type RuleRow,
  type Severity,
  SEVERITIES,
  useOutbox,
  useRules,
  useSaveRule,
} from '../../data/alerts';
import { relativeTime } from '../../lib/time';

const CHANNEL_ICON: Record<Channel, ReactNode> = {
  inbox: <InboxIcon fontSize="small" />,
  push: <PhoneIphoneIcon fontSize="small" />,
  email: <EmailIcon fontSize="small" />,
};
const SEVERITY_COLOR = { info: 'info', warning: 'warning', critical: 'error' } as const;

function toggle<T>(list: T[], item: T, on: boolean): T[] {
  return on ? [...new Set([...list, item])] : list.filter((x) => x !== item);
}

function RuleDialog({
  rule,
  saving,
  error,
  onSave,
  onClose,
}: {
  rule: RuleRow;
  saving: boolean;
  error: string | null;
  onSave(r: RuleRow): void;
  onClose(): void;
}) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'channels' | 'recipients'>('channels');
  const [delivery, setDelivery] = useState<Delivery>(() => structuredClone(rule.delivery));
  const set = (ch: Channel, patch: Partial<Delivery[Channel]>) =>
    setDelivery((d) => ({ ...d, [ch]: { ...d[ch], ...patch } }));
  const selected = CHANNELS.filter((ch) => delivery[ch].enabled);
  const name = t(`alert.rule.${rule.rule}`);
  // request kinds go to named people (requester, approvers, the source's staff), whatever their role
  const named = REQUEST_KINDS.includes(rule.rule);

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm" aria-label={name}>
      <DialogTitle sx={{ pb: 0 }}>
        <Typography variant="body2" color="text.secondary">
          {t('settings.notifications.editor_for')}
        </Typography>
        {name}
      </DialogTitle>
      <DialogContent>
        <Tabs value={tab} onChange={(_, v: typeof tab) => setTab(v)} sx={{ mb: 2 }}>
          <Tab value="channels" label={t('settings.notifications.tab_channels')} />
          <Tab value="recipients" label={t('settings.notifications.tab_recipients')} />
        </Tabs>

        {tab === 'channels' ? (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings.notifications.channels_help')}
            </Typography>
            <Paper variant="outlined" sx={{ p: 1 }}>
              {CHANNELS.map((ch) => (
                <Box key={ch} sx={{ px: 1, py: 0.5 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={delivery[ch].enabled}
                        onChange={(e) => set(ch, { enabled: e.target.checked })}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="center">
                        {CHANNEL_ICON[ch]}
                        <span>{t(`settings.notifications.channel_${ch}`)}</span>
                      </Stack>
                    }
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ ml: 4, mt: -0.5 }}>
                    {t(`settings.notifications.channel_${ch}_help`)}
                  </Typography>
                </Box>
              ))}
            </Paper>
            <Alert
              severity={selected.length ? 'success' : 'warning'}
              sx={{ mt: 2 }}
              data-testid="selected-channels"
            >
              {selected.length
                ? t('settings.notifications.selected_channels', {
                    list: selected
                      .map((ch) => t(`settings.notifications.channel_${ch}`))
                      .join(', '),
                  })
                : t('settings.notifications.no_channels')}
            </Alert>
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {t('settings.notifications.recipients_help')}
            </Typography>
            <Stack spacing={2}>
              {CHANNELS.map((ch) => {
                const c = delivery[ch];
                return (
                  <Paper key={ch} variant="outlined" sx={{ p: 2 }} data-testid={`recipients-${ch}`}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        {t(`settings.notifications.channel_${ch}`)}
                      </Typography>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={c.enabled}
                            onChange={(e) => set(ch, { enabled: e.target.checked })}
                          />
                        }
                        label={t('settings.notifications.enabled')}
                      />
                    </Stack>
                    <Divider sx={{ my: 1.5 }} />
                    {named ? (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        data-testid="named-recipients"
                      >
                        {t(`settings.notifications.who_${rule.rule}`)}
                      </Typography>
                    ) : (
                      <Typography variant="subtitle2">
                        {t('settings.notifications.allowed_roles')}
                      </Typography>
                    )}
                    <Stack direction="row" spacing={2} sx={named ? { display: 'none' } : undefined}>
                      {ROLES.map((role: Role) => (
                        <FormControlLabel
                          key={role}
                          control={
                            <Checkbox
                              checked={c.roles.includes(role)}
                              disabled={!c.enabled}
                              onChange={(e) =>
                                set(ch, { roles: toggle(c.roles, role, e.target.checked) })
                              }
                            />
                          }
                          label={t(`settings.notifications.role_${role}`)}
                        />
                      ))}
                    </Stack>
                    {c.enabled && !c.roles.length && !named ? (
                      <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ my: 1 }}>
                        {t('settings.notifications.no_roles')}
                      </Alert>
                    ) : null}
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>
                      {t('settings.notifications.severity_filter')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('settings.notifications.severity_help')}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                      {SEVERITIES.map((sev: Severity) => (
                        <FormControlLabel
                          key={sev}
                          control={
                            <Checkbox
                              checked={c.severities.includes(sev)}
                              disabled={!c.enabled}
                              onChange={(e) =>
                                set(ch, { severities: toggle(c.severities, sev, e.target.checked) })
                              }
                            />
                          }
                          label={
                            <Chip
                              size="small"
                              variant="outlined"
                              color={SEVERITY_COLOR[sev]}
                              label={t(`settings.notifications.severity_${sev}`)}
                            />
                          }
                        />
                      ))}
                    </Stack>
                    {c.enabled && !c.severities.length ? (
                      <Typography variant="body2" color="warning.main">
                        {t('settings.notifications.severity_all')}
                      </Typography>
                    ) : null}
                  </Paper>
                );
              })}
            </Stack>
          </>
        )}
        {error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={saving} onClick={() => onSave({ ...rule, delivery })}>
          {t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function RuleLine({
  rule,
  onSave,
  onEdit,
  saving,
}: {
  rule: RuleRow;
  onSave(r: RuleRow): void;
  onEdit(): void;
  saving: boolean;
}) {
  const { t } = useTranslation();
  const set = (patch: Partial<RuleRow>) => onSave({ ...rule, ...patch });
  const on = CHANNELS.filter((ch) => rule.delivery[ch].enabled);
  const threshold = Number(rule.params.threshold_pct ?? 15);
  // whole percent 1–100 (app.alerts_sweep casts it to integer); empty / invalid restores, unchanged saves nothing
  const saveThreshold = (input: HTMLInputElement) => {
    const n = Math.round(Number(input.value));
    const next =
      input.value.trim() === '' || !Number.isFinite(n) ? threshold : Math.min(100, Math.max(1, n));
    input.value = String(next);
    if (next !== threshold) set({ params: { ...rule.params, threshold_pct: next } });
  };
  return (
    <TableRow data-testid={`rule-${rule.rule}`}>
      <TableCell>{t(`alert.rule.${rule.rule}`)}</TableCell>
      <TableCell padding="checkbox">
        <Switch
          checked={rule.is_active}
          disabled={saving}
          onChange={(e) => set({ is_active: e.target.checked })}
          inputProps={{
            'aria-label': `${t(`alert.rule.${rule.rule}`)} ${t('settings.notifications.active')}`,
          }}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {on.length ? (
            on.map((ch) => (
              <Chip
                key={ch}
                size="small"
                icon={CHANNEL_ICON[ch] as ReactElement}
                label={t(`settings.notifications.channel_${ch}`)}
                variant="outlined"
              />
            ))
          ) : (
            <Typography variant="body2" color="warning.main">
              {t('settings.notifications.no_channels_short')}
            </Typography>
          )}
        </Stack>
      </TableCell>
      <TableCell>
        {rule.rule === 'left_site' ? (
          <Stack direction="row" alignItems="center" spacing={1}>
            <Switch
              size="small"
              checked={rule.params.only_outside_working_hours !== false}
              disabled={saving || !rule.is_active}
              onChange={(e) =>
                set({ params: { ...rule.params, only_outside_working_hours: e.target.checked } })
              }
            />
            <Typography variant="body2">{t('settings.notifications.only_after_hours')}</Typography>
          </Stack>
        ) : REQUEST_KINDS.includes(rule.rule) ? (
          <Typography variant="body2" color="text.secondary">
            {t(`settings.notifications.who_${rule.rule}`)}
          </Typography>
        ) : rule.rule === 'low_battery' ? (
          <TextField
            // re-mounts with the stored value when it changes (a refused save rolls back)
            key={threshold}
            size="small"
            type="number"
            label={t('settings.notifications.threshold_pct')}
            defaultValue={threshold}
            disabled={saving || !rule.is_active}
            slotProps={{ htmlInput: { min: 1, max: 100, step: 1 } }}
            onBlur={(e) => saveThreshold(e.target as HTMLInputElement)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            sx={{ width: 150 }}
          />
        ) : null}
      </TableCell>
      <TableCell align="right">
        <Button size="small" onClick={onEdit} disabled={!rule.is_active}>
          {t('settings.notifications.edit_delivery')}
        </Button>
      </TableCell>
    </TableRow>
  );
}

export function NotificationsPage() {
  const { t, i18n } = useTranslation();
  const { me } = useSession();
  const companyId = me?.company?.id;
  const rules = useRules(companyId);
  const saveRule = useSaveRule(companyId);
  const outbox = useOutbox();
  const [editing, setEditing] = useState<RuleRow | null>(null);

  return (
    <Page title={t('settings.notifications.title')}>
      <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 860 }}>
        {t('settings.notifications.intro')}
      </Typography>
      <Typography variant="h6" gutterBottom>
        {t('settings.notifications.rules_title')}
      </Typography>
      {rules.isLoading ? <Loading /> : null}
      {rules.error ? <LoadError error={rules.error} /> : null}
      {saveRule.error && !editing ? (
        <Alert severity="error">{(saveRule.error as Error).message}</Alert>
      ) : null}
      {rules.data ? (
        <Box sx={{ overflowX: 'auto', mb: 3 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.notifications.col_alert')}</TableCell>
                <TableCell>{t('settings.notifications.active')}</TableCell>
                <TableCell>{t('settings.notifications.col_channels')}</TableCell>
                <TableCell>{t('settings.notifications.col_options')}</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {rules.data.map((r) => (
                <RuleLine
                  key={r.rule}
                  rule={r}
                  saving={saveRule.isPending}
                  onSave={(next) => saveRule.mutate(next)}
                  onEdit={() => {
                    saveRule.reset();
                    setEditing(r);
                  }}
                />
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
      {editing ? (
        <RuleDialog
          rule={editing}
          saving={saveRule.isPending}
          error={saveRule.error ? (saveRule.error as Error).message : null}
          onClose={() => setEditing(null)}
          onSave={(next) => saveRule.mutate(next, { onSuccess: () => setEditing(null) })}
        />
      ) : null}

      <Typography variant="h6" gutterBottom>
        {t('settings.notifications.log_title')}
      </Typography>
      {outbox.data && !outbox.data.length ? (
        <Typography color="text.secondary">{t('settings.notifications.log_empty')}</Typography>
      ) : null}
      {outbox.data?.length ? (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('settings.notifications.col_when')}</TableCell>
                <TableCell>{t('settings.notifications.col_alert')}</TableCell>
                <TableCell>{t('settings.notifications.col_channel')}</TableCell>
                <TableCell>{t('settings.notifications.col_status')}</TableCell>
                <TableCell>{t('settings.notifications.col_error')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {outbox.data.map((o) => (
                <TableRow key={o.id}>
                  <TableCell>{relativeTime(o.created_at, i18n.language)}</TableCell>
                  <TableCell>
                    {t(`alert.rule.${o.template}`, {
                      defaultValue: t(`notify.${o.template}.title`, { defaultValue: o.template }),
                    })}
                  </TableCell>
                  <TableCell>{t(`settings.notifications.channel_${o.channel}`)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`settings.notifications.status_${o.status}`)}
                      color={
                        o.status === 'sent'
                          ? 'success'
                          : o.status === 'failed'
                            ? 'error'
                            : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell
                    sx={{
                      maxWidth: 320,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {o.last_error ?? ''}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      ) : null}
    </Page>
  );
}
